import { useEffect } from "react";
import Lenis from "@studio-freight/lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * useLenis
 * Initializes smooth scrolling site-wide and keeps GSAP ScrollTrigger
 * in sync with Lenis's virtual scroll position. Disabled entirely
 * when the user prefers reduced motion.
 */
export default function useLenis() {
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return undefined;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
    });

    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Route in-page anchor links (e.g. href="#services") through Lenis
    // instead of the browser's native jump, which would otherwise fight
    // Lenis's own frame-by-frame scroll position and cause stutter.
    const onClick = (e) => {
      const anchor = e.target.closest('a[href*="#"]');
      if (!anchor) return;

      const url = new URL(anchor.href, window.location.origin);
      if (url.pathname !== window.location.pathname || !url.hash) return;

      const target = document.querySelector(url.hash);
      if (!target) return;

      e.preventDefault();
      lenis.scrollTo(target, { offset: -88, duration: 1.2 });
    };
    document.addEventListener("click", onClick);

    // Give images/fonts a moment to settle, then recompute ScrollTrigger
    // measurements (pinned sections rely on accurate layout heights).
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener("load", refresh);
    const refreshTimeout = setTimeout(refresh, 500);

    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("load", refresh);
      clearTimeout(refreshTimeout);
      lenis.destroy();
    };
  }, []);
}
